import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import type { FramePreviewManifest } from "@/types/dragon-tiles";

import { frameVariantPreviewQueryOptions } from "@/api/dragon-tiles";

export interface EmailPreviewSourceArgs {
  initialPreview?: string;
  manifest?: FramePreviewManifest | null;
  siteId?: string;
  variantId?: string;
}

export function useEmailPreviewSource({
  initialPreview,
  manifest,
  siteId,
  variantId,
}: EmailPreviewSourceArgs) {
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const imageRef = useRef<HTMLImageElement | null>(null);

  const imageManifest = manifest?.state === "image" ? manifest : null;
  const showImage = imageManifest !== null && !imageFailed;

  // Grid/table use the lightweight desktop `card` render (FOG-1980 renders it at
  // desktop width). `full` stays available for a future expanded/hi-res view.
  const imageUrl = imageManifest?.card_url;

  const needsRenderedFallback = Boolean(
    manifest !== undefined &&
    initialPreview === undefined &&
    !showImage &&
    siteId &&
    variantId,
  );

  const { data: fetchedPreview } = useQuery({
    ...frameVariantPreviewQueryOptions(siteId ?? "", variantId ?? ""),
    enabled: needsRenderedFallback,
  });

  const previewBody =
    initialPreview ??
    (needsRenderedFallback ? fetchedPreview?.body : undefined);

  useEffect(() => {
    setIframeLoaded(false);
  }, [previewBody]);

  // A cached card image can finish (and fire load/error) before React flushes this
  // effect, so blindly resetting to "not loaded" would strand the card under its
  // skeleton with no second event coming. Seed from the element instead.
  useEffect(() => {
    const image = imageRef.current;
    if (!image?.complete) {
      setImageLoaded(false);
      setImageFailed(false);
      return;
    }
    const decoded = image.naturalWidth > 0;
    setImageLoaded(decoded);
    setImageFailed(!decoded);
  }, [imageUrl]);

  const contentLoaded = showImage ? imageLoaded : iframeLoaded;

  return {
    imageRef,
    imageUrl,
    showImage,
    previewBody,
    contentLoaded,
    iframeLoaded,
    imageLoaded,
    setIframeLoaded,
    setImageLoaded,
    setImageFailed,
  };
}
